import React from 'react';
import { getDataTable } from '../../charts/chartDataBindings';
import { SingleEntityTemplate } from '../../../../api/models/assets';
import { TpDataContext, TpDataTableColumn, isMultiValuedType } from '../../../../data/interfaces';
import styles from '../SingleItemView.module.scss';
import PagedTable, { TpPagedTableColumn, TpPagedTableData } from '../../../../components/pagedTable/PagedTable';
import { getCanonicalTypeInfo, getColumnInfo, performUserSelectionAction } from '../../../../data/analyticsDataSlice';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { useAppDispatch } from '../../../../app/hooks';
import { TpEnSelectionFilterTypes } from '../../../../data/selection/interfaces';
import { SectionIndex, TpSectionsIndexContext } from '../../../../components/sections-index/SectionsIndex';
import { TpSingleEntityViewContext } from '../interfaces';
import { getUserError } from '../../../../components/miscUtils';

function createLinkMatchInfo(colFrom: TpDataTableColumn, itemDataFrom: any, colTo: TpDataTableColumn): any {
  const isMultivaluedTo = isMultiValuedType(colTo.valueType);
  if (isMultiValuedType(colFrom.valueType)) {
    const valuesListFrom = itemDataFrom.map((value: any) => convertColumnDataToDisplayString(colFrom, value));
    const valueMapFrom: any = {};
    for (const value of valuesListFrom) valueMapFrom[value] = true;
    return {
      // Source data is single values => we just use that value to compare against
      isMultivaluedFrom: true,
      isMultivaluedTo,
      valueMapFrom,
      colTo,
    };
  } else {
    // Source data is multivalued => we build a map of all values for fast lookup
    return {
      isMultivaluedFrom: false,
      isMultivaluedTo,
      valueFrom: convertColumnDataToDisplayString(colFrom, itemDataFrom),
      colTo,
    };
  }
}

function evalLinkMatchInfo(linkMatchInfo: any, itemDataTo: any): boolean {
  if (!linkMatchInfo.isMultivaluedTo) {
    // Destination data is single valued
    const valueToStr = convertColumnDataToDisplayString(linkMatchInfo.colTo, itemDataTo);
    if (!linkMatchInfo.isMultivaluedFrom) {
      // Source data is single valued
      return valueToStr == linkMatchInfo.valueFrom;
    } else {
      // Source data is multi valued
      return linkMatchInfo.valueMapFrom[valueToStr];
    }
  } else {
    // Destination data is multi valued => we iterate over all values
    for (const valueTo of itemDataTo) {
      const valueToStr = convertColumnDataToDisplayString(linkMatchInfo.colTo, valueTo);
      if (!linkMatchInfo.isMultivaluedFrom) {
        // Source data is single valued
        if (valueToStr == linkMatchInfo.valueFrom) return true;
      } else {
        // Source data is multi valued
        if (linkMatchInfo.valueMapFrom[valueToStr]) return true;
      }
    }
    return false;
  }
}

interface TpRelatedTableProps {
  itemId: string;
  dataContext: TpDataContext;
  entityTypeTemplate?: SingleEntityTemplate;
  elementTemplate: any;
  children?: React.ReactNode;
  sectionIndexContext: TpSectionsIndexContext;
  singleEntityViewContext: TpSingleEntityViewContext;
}

interface TpSourceColInfo {
  id: string;
  width?: number;
}

interface TpElementTemplate {
  title: string;
  tableFrom: string;
  linkFrom: string;
  tableTo: string;
  linkTo: string;
  columnsTo: TpSourceColInfo[];
  sortBy?: string;
  sortDir?: string; // can be asc/desc
  openSectionOnSelectRelated?: string;
}

const RelatedTable = (props: TpRelatedTableProps) => {
  const dispatch = useAppDispatch();

  const { dataContext, itemId } = props;
  const elementTemplate = props.elementTemplate as TpElementTemplate;

  const tableFrom = getDataTable(dataContext, elementTemplate.tableFrom);
  const colInfoFrom = getColumnInfo(tableFrom, elementTemplate.linkFrom);
  const tableTo = getDataTable(dataContext, elementTemplate.tableTo);
  const canonicalTypeInfoTo = getCanonicalTypeInfo(dataContext, tableTo.canonicalTypeId);
  const colInfoTo = getColumnInfo(tableTo, elementTemplate.linkTo);

  const defaultSortColId = elementTemplate.sortBy || tableTo.colItemId;
  const defaultSortInv = elementTemplate.sortDir == 'desc';

  const itemIndexFrom = tableFrom.itemIdMap[props.itemId];
  if (itemIndexFrom === undefined) {
    return (
      <div>
        <div className={styles.tableTitle}>{`${elementTemplate.title}`}</div>
        <span>No data present</span>
      </div>
    );
  }
  const dataFrom = tableFrom.columnData[elementTemplate.linkFrom][itemIndexFrom];

  const linkMatchInfo = createLinkMatchInfo(colInfoFrom, dataFrom, colInfoTo);

  const pagedTableData: TpPagedTableData = {
    rowCount: 0,
    columns: [],
  };

  // Assemble the columns
  for (const colDef of elementTemplate.columnsTo) {
    const colInfo = getColumnInfo(tableTo, colDef.id);
    const tableCol: TpPagedTableColumn = {
      id: colDef.id,
      displayName: colInfo.name,
      data: [],
      width: colDef.width || 150,
      convertData2String: (val) => convertColumnDataToDisplayString(colInfo, val),
    };
    pagedTableData.columns.push(tableCol);
  }

  const relatedItemIds = tableTo.columnData[tableTo.colItemId];
  const matchingRelatedItemIds: any[] = [];
  const linkToData = tableTo.columnData[elementTemplate.linkTo];
  if (!linkToData) throw getUserError(`Column ${elementTemplate.linkTo} does not exist in ${tableTo.id}`);
  for (let itemIdx = 0; itemIdx < relatedItemIds.length; itemIdx++) {
    if (evalLinkMatchInfo(linkMatchInfo, linkToData[itemIdx])) {
      matchingRelatedItemIds.push(relatedItemIds[itemIdx]);
      pagedTableData.rowCount++;
      for (const col of pagedTableData.columns) {
        col.data.push(tableTo.columnData[col.id][itemIdx]);
      }
    }
  }

  function selectAllRelated() {
    dispatch(
      performUserSelectionAction({
        dataContextId: dataContext.id,
        canonicalTypeId: tableTo.canonicalTypeId,
        userSelectionAction: {
          type: TpEnSelectionFilterTypes.MANUAL,
          description: `Related to ${props.itemId}`,
          method: 'replace',
          selectedItemIds: matchingRelatedItemIds,
        },
      }),
    );
    if (elementTemplate.openSectionOnSelectRelated) {
      setTimeout(() => {
        props.singleEntityViewContext.closeEntityPopup();
        setTimeout(() => {
          document.getElementById(`_section_${elementTemplate.openSectionOnSelectRelated}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 50);
      }, 50);
    }
  }

  function onRowClicked(rowNr: number, rowData: any) {
    const relatedItemId = rowData[tableTo.colItemId];
    props.singleEntityViewContext.openEntity(canonicalTypeInfoTo.id, relatedItemId);
  }

  return (
    <SectionIndex
      sectionIndexContext={props.sectionIndexContext}
      sectionId={`relatedentities_${elementTemplate.tableFrom}_${elementTemplate.tableTo}`}
      sectionName={elementTemplate.title}
    >
      <div className={styles.tableTitle}>{`${elementTemplate.title} (${pagedTableData.rowCount})`}</div>
      {pagedTableData.rowCount == 0 && <div>No data found</div>}
      {pagedTableData.rowCount > 0 && (
        <>
          <FancyButton iconName="fa:circle-check" onClick={selectAllRelated}>
            {elementTemplate.openSectionOnSelectRelated ? 'Select & show in dashboard' : 'Select all related'}
          </FancyButton>
          <div style={{ height: '20px' }} />
          <PagedTable
            data={pagedTableData}
            defaultSortColId={defaultSortColId}
            defaultSortInv={defaultSortInv}
            onRowClicked={onRowClicked}
          />
        </>
      )}
    </SectionIndex>
  );
};

export default RelatedTable;
